import { defineStore } from 'pinia';
import cloneDeep from 'lodash/cloneDeep';
import setWith from 'lodash/setWith';
import camelCaseErrorMessageKeys from './functions/camel-case-error-message-keys';
import camelCaseDotString from '@/utils/camel-case-dot-string';
import { sequentialSaveStap } from './functions/sequential-save-stap';
import leadAanvraagApi from '@/api/lead-aanvraag.api';
import { useRootStore } from '@/stores/index';
import PageStatusEnum from '@/stores/lead-aanvraag/PageStatus.enum.js';
import getUtmCodesFromRoute from '@/stores/lead-aanvraag/functions/get-utm-codes-from-route';
import { useRouteExtensions } from '@/use/use-route-extensions';

export const useLeadAanvraagStore = defineStore('leadAanvraag', {
  state: () => ({
    errorMessages: {},
    loadingFromNext: false,
    loadingFromRouteParams: false,
    finalizePending: false,
    leadAanvraag: undefined,
    aanbodStarting: false,
    pageErrorMessages: []
  }),
  getters: {
    aanvraagId: (state) => state.leadAanvraag?.aanvraagId,
    loading: (state) => {
      return state.loadingFromNext || state.loadingFromRouteParams;
    },
    subflowState: (state) => (subflowNaam) => {
      return state.leadAanvraag?.subflowState?.find(
        (subflowState) => subflowState.subflowNaam === subflowNaam
      );
    },
    subflowStates: (state) => state.leadAanvraag?.subflowState,

    pageStatuses: (state) => (subflowNaam) => {
      if (!subflowNaam) return [];

      const pageStatusesObject =
        state?.leadAanvraag?.subflowState?.filter(
          (subflowState) => subflowState.subflowNaam === subflowNaam
        )?.[0]?.pageStatuses ?? undefined;

      if (!pageStatusesObject) return [];

      return Object.keys(pageStatusesObject).map((key) => ({
        pageNaam: key,
        pageStatus: pageStatusesObject[key].status
      }));
    },
    visiblePageStatuses() {
      return (subflowNaam) => {
        return this.pageStatuses(subflowNaam).filter(
          (status) =>
            status.pageStatus === PageStatusEnum.Open ||
            status.pageStatus === PageStatusEnum.Completed
        );
      };
    },
    visiblePageStatusProgress() {
      return (subflowNaam) => {
        if (!this.visiblePageStatuses) return [];

        return this.visiblePageStatuses(subflowNaam).map((status) => ({
          name: status.pageNaam,
          state: status.pageStatus === PageStatusEnum.Completed
        }));
      };
    },
    subflowVersieIds() {
      return this.leadAanvraag?.subflowVersies.map((sf) => sf.subflowVersieId);
    }
  },
  actions: {
    loadFromPageRoute(route) {
      return leadAanvraagApi.getLeadAanvraag({ ...route.params }, route.query).then((data) => {
        this.updateLeadAanvraag(data.leadAanvraag);
        //TO-IMPLEMENT this should be changed. Data also contains stap config and is therefore a side-effect
        //See adl (Pre data hook interceptors) for more information
        return data;
      });
    },
    loadFromRouteParams({ lang, aanbod, aanvraagId, updatePrijzen }) {
      this.resetAllErrorMessages();
      if ((aanvraagId && !this.leadAanvraag) || updatePrijzen) {
        this.loadingFromRouteParams = true;
        leadAanvraagApi
          .getLeadAanvraag({ aanbod, lang }, { aanvraagId }, updatePrijzen)
          .then((data) => {
            this.updateLeadAanvraag(data.leadAanvraag);
            this.loadingFromRouteParams = false;
          });
      }
    },

    save(update) {
      const routeExtensions = useRouteExtensions();
      return sequentialSaveStap(
        this.setValueOnLeadAanvraag,
        this.updateLeadAanvraag,
        routeExtensions.currentRoute.value.params,
        update
      );
    },
    async setAndSaveValueOnLeadAanvraag({ contentLocator, value }) {
      this.leadAanvraag = setWith(
        { ...this.leadAanvraag },
        camelCaseDotString(contentLocator),
        cloneDeep(value)
      );

      const routeExtensions = useRouteExtensions();
      await leadAanvraagApi.putLeadAanvraag(
        {
          aanbod: routeExtensions.currentRoute.value.params.aanbod,
          lang: routeExtensions.currentRoute.value.params.lang
        },
        this.leadAanvraag
      );
      return this.leadAanvraag;
    },
    async setAndSaveValueOnLeadAanvraagWithOverrideAanbodAndLang(
      { contentLocator, value },
      aanbod,
      lang
    ) {
      this.leadAanvraag = setWith(
        { ...this.leadAanvraag },
        camelCaseDotString(contentLocator),
        cloneDeep(value)
      );

      await leadAanvraagApi.putLeadAanvraag(
        {
          aanbod: aanbod,
          lang: lang
        },
        this.leadAanvraag
      );
      return this.leadAanvraag;
    },
    async updateAndSaveLeadAanvraag(leadAanvraag) {
      const routeExtensions = useRouteExtensions();
      this.leadAanvraag = leadAanvraag;
      await leadAanvraagApi.putLeadAanvraag(
        {
          aanbod: routeExtensions.currentRoute.value.params.aanbod,
          lang: routeExtensions.currentRoute.value.params.lang
        },
        this.leadAanvraag
      );
    },

    updateLeadAanvraag(leadAanvraag) {
      this.leadAanvraag = leadAanvraag;
    },
    reset() {
      this.errorMessages = {};
      this.pageErrorMessages = [];
      this.leadAanvraag = undefined;
    },
    resetAllErrorMessages() {
      this.errorMessages = {};
      this.pageErrorMessages = [];
    },
    setValueOnLeadAanvraag({ contentLocator, value }) {
      this.leadAanvraag = setWith(
        { ...this.leadAanvraag },
        camelCaseDotString(contentLocator),
        cloneDeep(value)
      );

      return this.leadAanvraag;
    },
    updateStapErrors(errorMessages) {
      this.errorMessages = camelCaseErrorMessageKeys(errorMessages);
    },
    updatePageErrors(pageErrorMessages) {
      this.pageErrorMessages = pageErrorMessages;
    },
    async startAanbod(aanbodType, aanbodVersieId, subflowVersies) {
      if (this.aanbodStarting) return Promise.reject('aanbod is already starting');

      this.aanbodStarting = true;

      const rootStore = useRootStore();
      const routeExtensions = useRouteExtensions();

      const utmCodes = getUtmCodesFromRoute(routeExtensions.currentRoute.value);
      const metrics = { gestartOnMobile: rootStore.isMobile };
      const response = await leadAanvraagApi.postLeadAanvraag(
        routeExtensions.currentRoute.value.params.lang,
        aanbodType,
        utmCodes,
        aanbodVersieId,
        subflowVersies,
        metrics
      );

      this.updateLeadAanvraag(response.leadAanvraag);
      this.aanbodStarting = false;
    }
  }
});
